// Панель поставщика программы.
//
// Уровень, которого клиент не видит и видеть не должен. Для завода программа —
// это его завод: никаких «других организаций» в ней нет, и намёка на них тоже.
// Поэтому страница не показывается в меню никому, кроме владельца платформы, а
// её адреса без права отвечают «не найдено», а не «нет прав»: отказ по правам
// сам по себе сообщение — он говорит, что возможность есть и кому-то её дают.
//
// Отсюда делают ровно три вещи:
//
//     завести клиента       новый завод купил программу
//     открыть ему цех       новый филиал — новый договор
//     выдать первого админа  дальше клиент распоряжается сам
//
// Всё остальное — люди, прессы, склады, настройки — внутри купленного делает
// администратор клиента. Мы туда не лезем: мы продали программу, а не работаем
// в цехе.
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { platformAPI, adminToolsAPI } from '../api';
import toast from 'react-hot-toast';
import { dangerConfirm } from '../components/ConfirmToast';

const INP = {
  width: '100%', padding: '9px 12px', background: 'var(--bg)',
  border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
  fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none',
};
const BTN = (bg, extra = {}) => ({
  background: bg, border: '1px solid ' + bg, borderRadius: 8, color: '#fff',
  padding: '9px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
  fontFamily: 'inherit', ...extra,
});
const GHOST = {
  ...BTN('transparent'), color: 'var(--muted)', borderColor: 'var(--border)',
};

const Field = ({ label, ...p }) => (
  <div>
    <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12,
                    marginBottom: 4 }}>{label}</label>
    <input {...p} style={{ ...INP, ...(p.style || {}) }} />
  </div>
);

// Клиент раскрывается: цеха, администраторы, и что с ними делать.
function OrgCard({ org, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [branch, setBranch] = useState({ name: '', city: '' });
  const [adm, setAdm] = useState({ username: '', password: '', first_name: '',
                                   last_name: '', branch: '' });

  // Какой цех сейчас переименовывают. null — никакой.
  const [editBranch, setEditBranch] = useState(null);

  const saveBranch = async () => {
    if (!editBranch.name.trim()) { toast.error('Укажите название цеха'); return; }
    setBusy(true);
    try {
      await platformAPI.saveBranch(org.id, {
        id: editBranch.id,
        name: editBranch.name.trim(),
        city: editBranch.city.trim(),
      });
      toast.success('Сохранено');
      setEditBranch(null);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Ошибка');
    } finally { setBusy(false); }
  };

  const addBranch = async () => {
    if (!branch.name.trim()) { toast.error('Укажите название цеха'); return; }
    setBusy(true);
    try {
      await platformAPI.addBranch(org.id, branch);
      toast.success(`Цех «${branch.name}» открыт`);
      setBranch({ name: '', city: '' });
      onChanged();
    } catch (e) { toast.error(e.response?.data?.error || 'Ошибка'); }
    finally { setBusy(false); }
  };

  const addAdmin = async () => {
    if (!adm.username.trim()) { toast.error('Укажите логин'); return; }
    if ((adm.password || '').length < 5) {
      toast.error('Пароль — не короче пяти символов'); return;
    }
    setBusy(true);
    try {
      const r = await platformAPI.addAdmin(org.id, adm);
      toast.success(`Администратор ${r.data.username} выдан`);
      setAdm({ username: '', password: '', first_name: '', last_name: '', branch: '' });
      onChanged();
    } catch (e) { toast.error(e.response?.data?.error || 'Ошибка'); }
    finally { setBusy(false); }
  };

  const resetPass = async (u) => {
    const pw = window.prompt(`Новый пароль для ${u.username}:`, '');
    if (pw === null) return;
    try {
      await platformAPI.resetPass(u.id, { password: pw });
      toast.success('Пароль выдан');
    } catch (e) { toast.error(e.response?.data?.error || 'Ошибка'); }
  };

  const toggleAutoplan = async (on) => {
    setBusy(true);
    try {
      await platformAPI.updateOrg(org.id, { autoplanner_enabled: on });
      toast.success(on ? 'Автопланировщик подключён' : 'Автопланировщик отключён');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.error || 'Ошибка'); }
    finally { setBusy(false); }
  };

  const noAdmin = (org.admins || []).length === 0;

  // Справочник у нового клиента кладётся сам. Кнопка нужна тем, кого завели
  // раньше: без сортов и причин остановки оператор у пресса встаёт и выбирать
  // ему не из чего.
  const seed = async () => {
    setBusy(true);
    try {
      const r = await platformAPI.seedOrg(org.id);
      const a = r.data.added || {};
      const n = (a.grades || 0) + (a.stops || 0) + (a.defects || 0);
      toast.success(n ? `Добавлено записей: ${n}` : 'Справочник уже на месте');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.error || 'Ошибка'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)',
                  borderRadius: 12, marginBottom: 12, overflow: 'hidden' }}>
      <button onClick={() => setOpen(v => !v)} style={{
        width: '100%', textAlign: 'left', background: 'none', border: 'none',
        padding: '14px 18px', cursor: 'pointer', fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      }}>
        <span style={{ color: 'var(--dim)', fontSize: 13 }}>{open ? '▾' : '▸'}</span>
        <span style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700 }}>
          {org.name}
        </span>
        {org.code && (
          <span style={{ color: 'var(--dim)', fontSize: 12 }}>{org.code}</span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8,
                       alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ background: 'var(--accent-weak)', color: 'var(--accent)',
                         borderRadius: 6, padding: '2px 10px', fontSize: 12,
                         fontWeight: 700 }}>
            цехов: {(org.branches || []).length}
          </span>
          <span style={{ color: 'var(--muted)', fontSize: 12 }}>
            людей: {org.users_total}
          </span>
          {/* Клиент заведён, а войти в него некому — видно сразу, не открывая */}
          {noAdmin && (
            <span style={{ background: '#ef444418', color: '#ef4444',
                           borderRadius: 6, padding: '2px 10px', fontSize: 12,
                           fontWeight: 700 }}>нет администратора</span>
          )}
          {org.autoplanner_enabled && (
            <span style={{ color: 'var(--accent)', fontSize: 12 }}>автопланировщик</span>
          )}
          {!org.is_active && (
            <span style={{ color: 'var(--dim)', fontSize: 12 }}>отключён</span>
          )}
        </span>
      </button>

      {open && (
        <div style={{ padding: '0 18px 18px', display: 'grid', gap: 18 }}>
          {/* ── Цеха ─────────────────────────────────────────────── */}
          <div>
            <div style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700,
                          textTransform: 'uppercase', marginBottom: 8 }}>
              Цеха
            </div>
            {(org.branches || []).length === 0 && (
              <div style={{ color: 'var(--dim)', fontSize: 13, marginBottom: 8 }}>
                Ни одного — клиенту ещё негде работать.
              </div>
            )}
            {(org.branches || []).map(b => (
              <div key={b.id} style={{
                display: 'flex', gap: 12, alignItems: 'center',
                padding: '7px 0', borderBottom: '1px solid var(--border)',
              }}>
                {/* Номер цеха — это его код в обмене с 1С: по нему бухгалтерия
                    сопоставляет цех со своим подразделением. Показываем прямо
                    здесь, чтобы не лезть за ним в базу. */}
                <span style={{ background: 'var(--bg)', color: 'var(--muted)',
                               borderRadius: 6, padding: '1px 8px', fontSize: 12,
                               fontFamily: 'monospace', fontWeight: 700 }}>
                  код {b.id}
                </span>
                {editBranch && editBranch.id === b.id ? (
                  <>
                    <input value={editBranch.name} autoFocus
                      onChange={e => setEditBranch({ ...editBranch, name: e.target.value })}
                      style={INP} />
                    <input value={editBranch.city}
                      onChange={e => setEditBranch({ ...editBranch, city: e.target.value })}
                      placeholder="Город" style={{ ...INP, maxWidth: 140 }} />
                    <button onClick={saveBranch} disabled={busy}
                      style={{ ...BTN('var(--accent)'), padding: '6px 12px', fontSize: 12 }}>
                      Сохранить
                    </button>
                    <button onClick={() => setEditBranch(null)}
                      style={{ background: 'none', border: 'none', color: 'var(--dim)',
                               fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Отмена
                    </button>
                  </>
                ) : (
                  <>
                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>{b.name}</span>
                    <span style={{ color: 'var(--dim)', fontSize: 12 }}>{b.city}</span>
                    {/* Название цеха видит вся смена — оно в шапке и в отчётах.
                        Задают его при открытии цеха, в спешке, а поправить до
                        сих пор можно было только в админке Django. */}
                    <button title="Переименовать"
                      onClick={() => setEditBranch({ id: b.id, name: b.name, city: b.city || '' })}
                      style={{ background: 'none', border: 'none', color: 'var(--dim)',
                               cursor: 'pointer', fontSize: 13, padding: 0 }}>
                      ✎
                    </button>
                    <span style={{ marginLeft: 'auto', color: 'var(--muted)',
                                   fontSize: 12 }}>людей: {b.users}</span>
                  </>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 10,
                          alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 2, minWidth: 180 }}>
                <Field label="Название цеха" value={branch.name}
                  onChange={e => setBranch({ ...branch, name: e.target.value })}
                  placeholder="AlmaTech Алматы" />
              </div>
              <div style={{ flex: 1, minWidth: 120 }}>
                <Field label="Город" value={branch.city}
                  onChange={e => setBranch({ ...branch, city: e.target.value })}
                  placeholder="Алматы" />
              </div>
              <button onClick={addBranch} disabled={busy} style={BTN('var(--accent)')}>
                Открыть цех
              </button>
            </div>
          </div>

          {/* ── Администраторы ───────────────────────────────────── */}
          <div>
            <div style={{ color: 'var(--muted)', fontSize: 12, fontWeight: 700,
                          textTransform: 'uppercase', marginBottom: 8 }}>
              Администраторы клиента
            </div>
            {(org.admins || []).map(u => (
              <div key={u.id} style={{
                display: 'flex', gap: 12, alignItems: 'center',
                padding: '7px 0', borderBottom: '1px solid var(--border)',
              }}>
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>
                  {u.username}
                </span>
                <span style={{ color: 'var(--muted)', fontSize: 12 }}>{u.name}</span>
                {u.all_branches && (
                  <span style={{ background: '#22c55e18', color: '#22c55e',
                                 borderRadius: 6, padding: '1px 8px', fontSize: 11,
                                 fontWeight: 700 }}>все цеха клиента</span>
                )}
                {!u.is_active && (
                  <span style={{ color: 'var(--dim)', fontSize: 12 }}>отключён</span>
                )}
                <button onClick={() => resetPass(u)} style={{
                  ...GHOST, marginLeft: 'auto', padding: '4px 12px', fontSize: 12,
                }}>Выдать пароль</button>
              </div>
            ))}
            {/* Первый человек нового клиента. Дальше завод заводит своих сам —
                операторов, технолога, склад: это его работа, а не наша. */}
            <div style={{ display: 'flex', gap: 8, marginTop: 10,
                          alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Field label="Логин" value={adm.username}
                  onChange={e => setAdm({ ...adm, username: e.target.value })} />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Field label="Пароль" value={adm.password}
                  onChange={e => setAdm({ ...adm, password: e.target.value })} />
              </div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <Field label="Фамилия и имя" value={adm.last_name}
                  onChange={e => setAdm({ ...adm, last_name: e.target.value })} />
              </div>
              <button onClick={addAdmin} disabled={busy} style={BTN('#8b5cf6')}>
                Выдать администратора
              </button>
            </div>
            <div style={{ marginTop: 14, paddingTop: 12,
                          borderTop: '1px solid var(--border)' }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center',
                              fontSize: 13, marginBottom: 12 }}>
                <input type="checkbox" checked={!!org.autoplanner_enabled}
                  disabled={busy}
                  onChange={e => toggleAutoplan(e.target.checked)} />
                Автопланировщик (модуль плана для этого клиента)
              </label>
              <button onClick={seed} disabled={busy} style={GHOST}>
                Доложить стандартный справочник
              </button>
              <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 10 }}>
                сорта, причины остановки и брака — если клиент заведён давно
              </span>
            </div>
            <div style={{ color: 'var(--dim)', fontSize: 12, marginTop: 8,
                          lineHeight: 1.5, maxWidth: 700 }}>
              Он получает права хозяина всего купленного: заводит своих людей,
              прессы, склады и настройки — во всех цехах этого клиента. Новый цех
              открыть не может: этой кнопки у него нет.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Опасная зона. Стоит здесь, а не у администратора завода.
//
// Раньше «Очистка системы» жила в его же настройках, рядом с обычными
// галочками. Он приходит туда за «начать заново» — а кнопка сносит справочник
// номенклатуры и рецепты, которые технолог собирал месяц. Завод при этом
// теряет не тестовые данные, а работу.
//
// Снимок базы делается САМ, до удаления. Не «не забудьте сделать копию»: в тот
// момент, когда эту кнопку жмут, человек торопится начать с чистого листа и о
// копии не думает. Поэтому копия — не его обязанность.
function DangerZone() {
  const [info, setInfo] = useState(null);
  const [snaps, setSnaps] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        adminToolsAPI.systemInfo(), adminToolsAPI.backups(),
      ]);
      setInfo(a.data); setSnaps(b.data);
    } catch { setInfo(null); setSnaps(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const snap = async () => {
    setBusy('snap');
    try {
      const { data } = await adminToolsAPI.makeBackup('вручную');
      toast.success(`Снимок сделан · ${data.item.size_h}`);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Ошибка'); }
    finally { setBusy(''); }
  };

  const wipe = async () => {
    const c = info?.counts || {};
    if (!await dangerConfirm({
      title: 'Очистка системы',
      finalText: 'Да, очистить всё',
      steps: [
        'Будет удалено ВСЁ содержимое базы:\n'
        + Object.entries(c).map(([k, v]) => `  ${k}: ${v}`).join('\n')
        + '\n\nОстанутся только пользователи и филиалы.',

        'Это база работающего завода. Вместе с ней исчезают справочник '
        + 'номенклатуры и рецепты — то, что технолог собирал руками месяцами, '
        + 'и то, без чего производство не спишет ни листа сырья.',

        'Снимок базы система сделает сама, прямо перед удалением, и вернуться '
        + 'к нему можно будет кнопкой ниже.\n\nВсё, что сделают после '
        + 'очистки, при возврате исчезнет.',
      ],
    })) return;
    setBusy('wipe');
    try {
      const { data } = await adminToolsAPI.systemReset();
      toast.success(data.message || 'Система очищена');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Ошибка'); }
    finally { setBusy(''); }
  };

  const back = async (row) => {
    if (!await dangerConfirm({
      title: 'Восстановить базу',
      finalText: 'Да, восстановить',
      steps: [
        `База вернётся к состоянию на ${row.at_h}.`,

        'Всё, что сделали после этого момента, исчезнет: заказы, смены, '
        + 'приёмки, отгрузки. Если завод работал — работа за это время '
        + 'пропадёт.',

        'Перед возвратом система снимет ещё один снимок — сегодняшний. Если '
        + 'вернулись не туда, будет куда вернуться обратно.',
      ],
    })) return;
    setBusy('back');
    try {
      await adminToolsAPI.restore(row.name);
      toast.success(`База восстановлена на ${row.at_h}`);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) { toast.error(e.response?.data?.error || 'Ошибка'); }
    finally { setBusy(''); }
  };

  if (!info) return null;

  const rows = (snaps && snaps.items) || [];
  const noTool = snaps && snaps.available === false;

  return (
    <div style={{ border: '1px solid #ef4444', borderRadius: 12, padding: 16,
                  marginTop: 26, background: 'var(--card)' }}>
      <h3 style={{ color: '#ef4444', fontSize: 15, fontWeight: 700,
                   margin: '0 0 6px' }}>⚠️ Опасная зона</h3>
      <p style={{ color: 'var(--dim)', fontSize: 12.5, lineHeight: 1.6,
                  margin: '0 0 14px', maxWidth: 720 }}>
        Очистка отдаёт пустую базу — так готовят её новому клиенту. У завода
        этой кнопки нет: он обнуляет только остаток, не трогая работу.
      </p>

      <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px',
                    fontSize: 13, color: 'var(--muted)', marginBottom: 14,
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
                    gap: '4px 16px' }}>
        {Object.entries(info.counts || {}).map(([k, v]) => (
          <div key={k}>{k}: <b style={{ color: 'var(--text)' }}>{v}</b></div>
        ))}
      </div>

      {noTool && (
        <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 12 }}>
          Снимок базы сделать нечем — очистка заблокирована. Нужен образ с
          postgresql-client.
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={snap} disabled={!!busy || noTool} style={GHOST}>
          {busy === 'snap' ? 'Снимаю…' : 'Снять снимок базы'}
        </button>
        <button onClick={wipe} disabled={!!busy || noTool}
                style={BTN('#ef4444')}>
          {busy === 'wipe' ? 'Очистка…' : 'Очистить систему'}
        </button>
      </div>

      <div style={{ marginTop: 16, borderTop: '1px solid var(--border)',
                    paddingTop: 12 }}>
        <div style={{ color: 'var(--muted)', fontSize: 12.5, marginBottom: 8 }}>
          Снимки базы {rows.length ? '' : '— пока ни одного'}
        </div>
        {rows.map(r => (
          <div key={r.name} style={{ display: 'flex', alignItems: 'center',
                                     gap: 12, padding: '6px 0', fontSize: 13 }}>
            <span style={{ color: 'var(--text)', minWidth: 130 }}>{r.at_h}</span>
            <span style={{ color: 'var(--dim)' }}>{r.size_h}</span>
            <button onClick={() => back(r)} disabled={!!busy}
                    style={{ ...GHOST, padding: '5px 12px', fontSize: 12.5,
                             marginLeft: 'auto' }}>
              Восстановить на это время
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PlatformPage() {
  const [rows, setRows] = useState(null);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [autoplan, setAutoplan] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await platformAPI.orgs();
      setRows(r.data.results || []);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addOrg = async () => {
    if (!name.trim()) { toast.error('Укажите название'); return; }
    setBusy(true);
    try {
      await platformAPI.createOrg({ name, code, autoplanner_enabled: autoplan });
      toast.success(`Клиент «${name}» заведён`);
      setName(''); setCode(''); setAutoplan(false);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Ошибка'); }
    finally { setBusy(false); }
  };

  return (
    <Layout title="Платформа">
      <div style={{ maxWidth: 1000 }}>
        <div style={{ background: 'var(--accent-weak)',
                      border: '1px solid var(--border)', borderRadius: 10,
                      padding: '11px 15px', marginBottom: 16, fontSize: 12.5,
                      color: 'var(--muted)', lineHeight: 1.6 }}>
          Отсюда заводят клиента, открывают ему цех и выдают первого
          администратора. Всё остальное клиент делает у себя сам.
          <br />
          Эту страницу видите только вы: у завода её нет, и о том, что новый цех
          вообще можно открыть, он не знает.
        </div>

        <div style={{ background: 'var(--card)', border: '1px solid var(--border)',
                      borderRadius: 12, padding: 16, marginBottom: 18,
                      display: 'flex', gap: 10, alignItems: 'flex-end',
                      flexWrap: 'wrap' }}>
          <div style={{ flex: 2, minWidth: 200 }}>
            <Field label="Новый клиент" value={name}
              onChange={e => setName(e.target.value)} placeholder="AlmaTech" />
          </div>
          <div style={{ flex: 1, minWidth: 130 }}>
            <Field label="Код" value={code}
              onChange={e => setCode(e.target.value)} placeholder="almatech" />
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center',
                          fontSize: 13, color: 'var(--muted)' }}>
            <input type="checkbox" checked={autoplan}
              onChange={e => setAutoplan(e.target.checked)} />
            Автопланировщик
          </label>
          <button onClick={addOrg} disabled={busy} style={BTN('var(--accent)')}>
            Завести
          </button>
        </div>

        {rows === null && (
          <div style={{ color: 'var(--muted)', padding: 20 }}>Загрузка…</div>
        )}
        {rows !== null && rows.length === 0 && (
          <div style={{ color: 'var(--dim)', padding: 20 }}>Клиентов пока нет.</div>
        )}
        {(rows || []).map(o => (
          <OrgCard key={o.id} org={o} onChanged={load} />
        ))}

        <DangerZone />
      </div>
    </Layout>
  );
}
